import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pushEvent } from '../utils/gtm';
import { isGtmEnabled } from '../utils/getGtmDomain';
import { getAttribution } from '../utils/attribution';
import { SS_SCROLL_DEPTH_SENT } from '../configs/storageKeys';
import { GA4_EVENTS, SCROLL_DEPTH_THRESHOLDS } from '../constants/ga4Events';

/**
 * Fires scroll_depth at 25/50/75/90% — each threshold once per pathname per session.
 * Custom implementation because GA4 Enhanced Measurement only tracks ~90% by default.
 * Non-scrollable pages emit nothing (no depth to measure). Must render inside BrowserRouter.
 */
export function ScrollDepthTracker() {
  const { pathname } = useLocation();
  // In-memory guard so we don't touch sessionStorage on every scroll frame.
  const firedRef = useRef<Set<number>>(new Set());
  // True only for the very first effect run (initial document load). SPA route
  // changes flip this off so we never measure depth from a stale scrollY.
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    // GTM off → no events would ever be sent; skip all listeners and per-frame work.
    if (!isGtmEnabled()) return;

    firedRef.current = new Set();
    let rafId = 0;
    let ticking = false;

    const sendThreshold = (threshold: number) => {
      if (firedRef.current.has(threshold)) return;

      const key = `${SS_SCROLL_DEPTH_SENT}:${pathname}:${threshold}`;
      let alreadySent = false;
      try { alreadySent = !!sessionStorage.getItem(key); } catch {}
      if (alreadySent) { firedRef.current.add(threshold); return; }

      // current_path comes from the shared attribution context.
      const sent = pushEvent(GA4_EVENTS.SCROLL_DEPTH, {
        scroll_percent: threshold,
        ...getAttribution(),
      });
      if (sent) {
        firedRef.current.add(threshold);
        try { sessionStorage.setItem(key, '1'); } catch {}
      }
    };

    const check = () => {
      ticking = false;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return; // nothing scrollable → no depth
      const percent = (window.scrollY / docHeight) * 100;
      for (const threshold of SCROLL_DEPTH_THRESHOLDS) {
        if (percent >= threshold) sendThreshold(threshold);
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      rafId = window.requestAnimationFrame(check);
    };

    // Initial deferred check runs only on first load to catch a browser-restored scroll
    // (reload / back-forward). On SPA route changes scrollY is stale, so we skip it and
    // wait for real scrolls. We also don't recompute on resize: layout shifts without
    // scrolling (FAQ, mobile keyboard) would mis-report depth.
    if (isInitialLoadRef.current) {
      rafId = window.requestAnimationFrame(check);
      isInitialLoadRef.current = false;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
    };
  }, [pathname]);

  return null;
}
